import { MESSAGES_LIMIT } from 'widget/consts/api';
import { API_ENDPOINTS } from 'widget/consts/endpoints';
import type { BaseResponse } from 'widget/types/api';

import { BaseApiStore } from '../BaseApiStore';

import {
  OpenSessionResponse,
  ObtainWsTokenResponse,
  ObtainSubscriptionTokenResponse,
  GetMessagesHistoryResponse,
  SendMessageResponse,
  UploadAttachmentResponse,
  WidgetConfigResponse,
  SubmitConsentsRequest,
  SubmitConsentsResponse,
  SubmitContactsRequest,
  SubmitContactsResponse
} from './types';

export class ApiStore extends BaseApiStore {
  async openSession(
    initToken: string
  ): Promise<BaseResponse<OpenSessionResponse>> {
    return await this.api<OpenSessionResponse>({
      endpoint: API_ENDPOINTS.widgets.sessions.open,
      params: {
        method: 'POST',
        data: {
          init_token: initToken
        }
      }
    });
  }

  obtainWsToken = async (): Promise<BaseResponse<ObtainWsTokenResponse>> => {
    return await this.api<ObtainWsTokenResponse>({
      endpoint: API_ENDPOINTS.widgets.ws.token,
      params: {
        method: 'GET'
      }
    });
  };

  getSubscriptionToken = async (): Promise<
    BaseResponse<ObtainSubscriptionTokenResponse>
  > => {
    return await this.api<ObtainSubscriptionTokenResponse>({
      endpoint: API_ENDPOINTS.widgets.ws.subscriptionToken,
      params: {
        method: 'GET'
      }
    });
  };

  getMessagesHistory = async (params?: {
    limit?: number;
    from_id?: string;
  }): Promise<BaseResponse<GetMessagesHistoryResponse>> => {
    return await this.api<GetMessagesHistoryResponse>({
      endpoint: API_ENDPOINTS.widgets.messages.history,
      params: {
        method: 'GET',
        data: {
          limit: params?.limit ?? MESSAGES_LIMIT,
          ...(params?.from_id && { from_id: params.from_id })
        }
      }
    });
  };

  sendMessage = async (
    text: string,
    attachmentIds?: string[]
  ): Promise<BaseResponse<SendMessageResponse>> => {
    return await this.api<SendMessageResponse>({
      endpoint: API_ENDPOINTS.widgets.messages.add,
      params: {
        method: 'POST',
        data: {
          ...(text && { text }),
          ...(attachmentIds &&
            attachmentIds.length > 0 && {
              attachment_ids: attachmentIds
            })
        }
      }
    });
  };

  uploadAttachment = async (
    file: File,
    signal?: AbortSignal
  ): Promise<BaseResponse<UploadAttachmentResponse>> => {
    const formData = new FormData();
    formData.append('file', file);

    return await this.api<UploadAttachmentResponse>({
      endpoint: API_ENDPOINTS.widgets.attachments.upload,
      params: {
        method: 'POST',
        formData,
        signal
      }
    });
  };

  getWidgetConfig = async (
    initToken: string | null = null,
    options?: { omitAuthToken?: boolean }
  ): Promise<BaseResponse<WidgetConfigResponse, string>> => {
    return await this.api<WidgetConfigResponse>({
      endpoint: API_ENDPOINTS.widgets.config,
      params: {
        method: 'GET',
        ...(options?.omitAuthToken && { omitAuthToken: true }),
        ...(initToken && { headers: { 'X-Init-Token': initToken } })
      }
    });
  };

  submitConsents = async (
    data: SubmitConsentsRequest
  ): Promise<BaseResponse<SubmitConsentsResponse>> => {
    return await this.api<SubmitConsentsResponse>({
      endpoint: API_ENDPOINTS.widgets.consents.submit,
      params: {
        method: 'POST',
        data
      }
    });
  };

  submitContacts = async (
    data: SubmitContactsRequest
  ): Promise<BaseResponse<SubmitContactsResponse>> => {
    return await this.api<SubmitContactsResponse>({
      endpoint: API_ENDPOINTS.widgets.contacts.submit,
      params: {
        method: 'POST',
        data
      }
    });
  };
}
